import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Send, Tag, X, ImagePlus, BarChart2, Moon } from 'lucide-react';
import * as api from '../lib/api';
import { defaultAvatar } from '../lib/avatarDefaults';
import IslamojiPicker from './IslamojiPicker';
import { prepareImage } from '../lib/imageResize';

const TAGS = ['Spiritual Growth', 'Advice', 'Win of the Day', 'Question', 'Reflection'];
const SPECIAL_TAGS = {
  Founder: { label: 'Founder', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  Mentor: { label: 'Mentor', className: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300' },
};
const MAX_IMAGE_BYTES = 1.9 * 1024 * 1024; // the server accepts up to 2MB; photos are shrunk to fit

const PostComposer = ({ groupId, spaceId, onPostCreated, isSelfFeed, challengePreFill, onChallengeConsumed }) => {
  const { profile } = useAuth();
  const [content, setContent] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTags, setShowTags] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [islamojiGifUrl, setIslamojiGifUrl] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const [showIslamojis, setShowIslamojis] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState(['', '']);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle challenge pre-fill
  useEffect(() => {
    if (challengePreFill) {
      setContent(`\u201C${challengePreFill}\u201D\n\n`);
      onChallengeConsumed?.();
      setTimeout(() => textareaRef.current?.focus(), 200);
    }
  }, [challengePreFill, onChallengeConsumed]);

  const handleImageSelect = async (e) => {
    const picked = e.target.files?.[0];
    if (!picked) return;

    if (!['image/jpeg', 'image/png'].includes(picked.type)) {
      alert('Only JPEG and PNG images are allowed.');
      return;
    }

    // Shrink big photos in the browser so they upload quickly and fit the 2MB limit
    let file;
    try {
      ({ file } = await prepareImage(picked, { maxDimension: 1600, maxBytes: MAX_IMAGE_BYTES }));
    } catch (err) {
      alert(err.message || 'We could not use that photo. Please choose a different one.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setIslamojiGifUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSelectIslamojiEmoji = (item) => {
    const shortcode = `:${item.name}:`;
    const el = textareaRef.current;
    if (el) {
      const start = el.selectionStart ?? content.length;
      const end = el.selectionEnd ?? content.length;
      const newContent = content.slice(0, start) + shortcode + content.slice(end);
      setContent(newContent);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + shortcode.length;
        el.setSelectionRange(pos, pos);
      });
    } else {
      setContent(c => c + shortcode);
    }
  };

  const handleSelectIslamojiGif = (item) => {
    setImageFile(null);
    setImagePreview(item.image_url);
    setIslamojiGifUrl(item.image_url);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const filledPollOptions = pollOptions.map(o => o.trim()).filter(Boolean);
  const hasValidPoll = showPoll && filledPollOptions.length >= 2;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if ((!content.trim() && !imageFile && !islamojiGifUrl && !hasValidPoll) || loading) return;

    if (!profile?.id) {
      alert('Please sign in to post');
      return;
    }

    if (showPoll && filledPollOptions.length < 2) {
      alert('A poll needs at least 2 options.');
      return;
    }

    setLoading(true);
    try {
      let imageUrl = islamojiGifUrl || null;

      // Upload image first if present (an Islamoji gif already has a URL — no upload needed)
      if (imageFile) {
        setUploadProgress('Uploading image...');
        const formData = new FormData();
        formData.append('file', imageFile);
        const uploadRes = await api.uploadPostImage(formData);
        imageUrl = uploadRes.data.url;
      }

      setUploadProgress('');
      const postData = {
        content: content.trim() || ' ',
        group_id: groupId || null,
        space_id: spaceId || null,
        tags: selectedTags,
        is_self_post: isSelfFeed || false,
        image_url: imageUrl,
        ...(hasValidPoll ? { poll: { options: filledPollOptions } } : {}),
      };
      await api.createPost(postData, profile.id);
      setContent('');
      setSelectedTags([]);
      setShowTags(false);
      setShowPoll(false);
      setPollOptions(['', '']);
      clearImage();
      onPostCreated?.();
    } catch (err) {
      console.error('Error creating post:', err);
      const timedOut = err?.code === 'ECONNABORTED' || (!err?.response && /timeout/i.test(err?.message || ''));
      const detail = err?.response?.data?.detail
        || (timedOut ? 'Your post is taking longer than expected to send. Please check your connection and try again.' : 'Failed to create post. Please try again.');
      alert(detail);
    } finally {
      setLoading(false);
      setUploadProgress('');
    }
  };

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card rounded-arch-xl p-6 mb-6"
      data-testid="post-composer"
    >
      {isSelfFeed && (
        <p className="text-xs text-secondary mb-3 font-medium" data-testid="self-post-label">
          This post will only be visible to you
        </p>
      )}
      <div className="flex gap-3 sm:gap-4">
        <img
          src={profile?.avatar_url || defaultAvatar(profile?.gender)}
          alt={profile?.display_name}
          className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover border-2 border-primary flex-shrink-0"
        />
        <form onSubmit={handleSubmit} className="flex-1 min-w-0">
          <textarea
            ref={textareaRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={isSelfFeed
              ? "Write something for yourself — a scripture, affirmation, or reflection..."
              : "Share your thoughts, ask a question, or encourage someone..."}
            className={`w-full p-4 bg-white/70 dark:bg-white dark:text-gray-800 dark:placeholder:text-primary rounded-arch-lg border border-primary/20 focus:ring-2 focus:ring-primary resize-none transition-all min-h-[100px] text-text-main placeholder:text-text-muted ${
              content.startsWith('\u201C') ? 'italic' : ''
            }`}
            data-testid="post-content-input"
          />

          {/* Image Preview */}
          {imagePreview && (
            <div className="relative mt-3 inline-block">
              <img src={imagePreview} alt="Upload preview" className="max-h-48 rounded-arch-lg border border-primary/20 object-contain" />
              <button type="button" onClick={clearImage}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full shadow"
                data-testid="clear-image-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Upload progress */}
          {uploadProgress && (
            <p className="text-xs text-primary mt-2 animate-pulse">{uploadProgress}</p>
          )}

          {/* Islamoji picker */}
          {showIslamojis && (
            <IslamojiPicker
              onSelectEmoji={handleSelectIslamojiEmoji}
              onSelectGif={handleSelectIslamojiGif}
            />
          )}

          {/* Poll builder */}
          {showPoll && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-3 p-3 bg-white/50 dark:bg-white/10 rounded-arch-lg border border-primary/20"
            >
              <p className="text-xs font-medium text-text-muted mb-2">Poll options</p>
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => {
                      const updated = [...pollOptions];
                      updated[i] = e.target.value;
                      setPollOptions(updated);
                    }}
                    placeholder={`Option ${i + 1}`}
                    maxLength={100}
                    className="flex-1 px-3 py-1.5 bg-white/70 dark:bg-white dark:text-gray-800 rounded-arch border border-primary/20 text-sm text-text-main placeholder:text-text-muted focus:ring-2 focus:ring-primary"
                  />
                  {pollOptions.length > 2 && (
                    <button type="button"
                      onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}
                      className="p-1 text-red-400 hover:text-red-500 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button type="button"
                  onClick={() => setPollOptions(prev => [...prev, ''])}
                  className="text-xs text-primary hover:underline mt-1">
                  + Add option
                </button>
              )}
            </motion.div>
          )}

          {/* Selected Tags */}
          {selectedTags.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {selectedTags.map((tag) => {
                const special = SPECIAL_TAGS[tag];
                return (
                  <span key={tag} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${special ? special.className : 'bg-secondary/30 text-secondary'}`}>
                    {tag}
                    <button type="button" onClick={() => toggleTag(tag)} className="hover:opacity-70 ml-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Tag Selector */}
          {showTags && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="flex flex-wrap gap-2 mt-3 p-3 bg-white/50 rounded-arch-lg"
            >
              {TAGS.filter((tag) => !selectedTags.includes(tag)).map((tag) => (
                <button key={tag} type="button" onClick={() => toggleTag(tag)}
                  className="px-3 py-1 border border-primary/30 rounded-full text-sm text-text-muted hover:bg-primary/10 hover:text-primary transition-colors"
                  data-testid={`tag-option-${tag.toLowerCase().replace(/\s/g, '-')}`}>
                  {tag}
                </button>
              ))}
              {profile?.is_admin && !selectedTags.includes('Founder') && (
                <button type="button" onClick={() => toggleTag('Founder')}
                  className="px-3 py-1 border border-amber-300 rounded-full text-sm text-amber-700 hover:bg-amber-50 transition-colors"
                  data-testid="tag-option-founder">
                  Founder
                </button>
              )}
              {profile?.is_mentor && !selectedTags.includes('Mentor') && (
                <button type="button" onClick={() => toggleTag('Mentor')}
                  className="px-3 py-1 border border-rose-300 rounded-full text-sm text-rose-600 hover:bg-rose-50 transition-colors"
                  data-testid="tag-option-mentor">
                  Mentor
                </button>
              )}
            </motion.div>
          )}

          <div className="flex items-center justify-between mt-4 gap-2">
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setShowTags(!showTags)}
                className="flex items-center gap-1.5 px-2 sm:px-4 py-2 text-text-muted hover:text-primary hover:bg-primary/10 rounded-arch transition-colors"
                data-testid="add-tags-btn">
                <Tag className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Tags</span>
              </button>

              {/* Image upload button */}
              <label className="flex items-center gap-1.5 px-2 sm:px-4 py-2 text-text-muted hover:text-primary hover:bg-primary/10 rounded-arch transition-colors cursor-pointer"
                data-testid="add-image-btn">
                <ImagePlus className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Image</span>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleImageSelect} className="hidden" />
              </label>

              {/* Islamojis toggle button */}
              <button type="button"
                onClick={() => setShowIslamojis(s => !s)}
                className={`flex items-center gap-1.5 px-2 sm:px-4 py-2 rounded-arch transition-colors ${showIslamojis ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                data-testid="add-islamoji-btn">
                <Moon className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Islamojis</span>
              </button>

              {/* Poll toggle button */}
              <button type="button"
                onClick={() => { setShowPoll(p => !p); if (showPoll) setPollOptions(['', '']); }}
                className={`flex items-center gap-1.5 px-2 sm:px-4 py-2 rounded-arch transition-colors ${showPoll ? 'text-primary bg-primary/10' : 'text-text-muted hover:text-primary hover:bg-primary/10'}`}
                data-testid="add-poll-btn">
                <BarChart2 className="w-4 h-4 flex-shrink-0" />
                <span className="hidden sm:inline">Poll</span>
              </button>
            </div>

            <button type="submit" disabled={(!content.trim() && !imageFile && !islamojiGifUrl && !hasValidPoll) || loading}
              className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-6 py-2.5 bg-primary text-primary-foreground rounded-full font-medium btn-hover disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              data-testid="submit-post-btn">
              {loading ? (uploadProgress ? 'Uploading...' : 'Posting...') : 'Post'}
              <Send className="w-4 h-4" />
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default PostComposer;
